import { SCHEMAS_DE_GARDE, type FamilleSchema, type Messages, type SchemaDeGarde } from '@repere/core';
import { useRef, useState, type ReactNode } from 'react';
import { Alerte, Bouton, Carte, Champ, ChampDate, Invisible } from '../ui';
import { ChampDateNaissance } from '../ui/ChampDateNaissance';
import { messageDErreur, useSession } from '../auth/session';
import { maintenant } from '../horloge';
import { ApercuAlternance } from '../dossier/ApercuAlternance';
import { ChampResidence, residenceParDefaut, type Residence } from '../dossier/ChampResidence';
import { useTraduction } from '../i18n/langue';

interface EnfantSaisi {
  /**
   * Identité de la ligne, propre au formulaire — elle ne part jamais au serveur.
   *
   * Elle est nécessaire au retrait : les trois listes d'une date de naissance vivent en
   * état local (voir `ChampDate`), et ne se resynchronisent que sur une valeur non vide.
   * Repérées par leur rang, retirer le premier enfant de deux ferait glisser le second
   * d'un cran et laisserait sa date affichée sur celle qu'on venait de supprimer.
   */
  cle: number;
  firstName: string;
  birthDate: string;
}

let prochaineCle = 0;

function enfantVide(): EnfantSaisi {
  prochaineCle += 1;
  return { cle: prochaineCle, firstName: '', birthDate: '' };
}

interface DossierCree {
  id: string;
}

/**
 * Les familles proposées, dans leur ordre d'affichage. La liste vient de `@repere/core` :
 * ce qui est offert ici est exactement ce que le serveur accepte. Le titre de chacune se
 * traduit — voir `messages.schemasDeGarde.familles` — et ne vit donc pas ici.
 *
 * Le « sur mesure » n'est pas proposé à la création. Un parent qui ouvre son dossier
 * décrit la situation qu'il vit ; lui offrir d'emblée un calendrier vide à remplir jour
 * par jour serait lui demander de faire le travail du produit.
 */
const ORDRE_FAMILLES: readonly FamilleSchema[] = [
  'alternance',
  'alternance_inegale',
  'residence_principale',
  'autre',
];

const SCHEMAS = SCHEMAS_DE_GARDE.filter((s) => s.kind !== 'custom');

/**
 * La date du jour au format de l'API, `AAAA-MM-JJ`.
 *
 * Composée à la main plutôt que par `toISOString()`, qui rend l'heure UTC : en France,
 * un dossier créé après 22 h aurait démarré la veille. Ce qui est demandé ici est un jour
 * de calendrier tel que le parent le lit sur son téléphone, pas un instant.
 */
function aujourdhui(): string {
  const jour = maintenant();
  const mois = String(jour.getMonth() + 1).padStart(2, '0');
  return `${jour.getFullYear()}-${mois}-${String(jour.getDate()).padStart(2, '0')}`;
}

/**
 * La progression, montrée plutôt qu'écrite.
 *
 * « Étape 2 sur 4 » demande de compter pour savoir ce qui reste, et ne dit rien de ce qui
 * attend. Quatre segments nommés répondent aux deux questions d'un coup d'œil : où j'en
 * suis, et ce qu'il reste à faire.
 *
 * **La couleur ne porte rien à elle seule.** Un segment franchi et un segment à venir se
 * distinguent aussi par la graisse de leur libellé, et leur état part en toutes lettres
 * dans la partie invisible — un parent daltonien, comme un lecteur d'écran, lit la même
 * chose que les autres (WCAG 1.4.1).
 *
 * L'énoncé « Étape N sur 4 » est conservé, invisible : c'est la phrase la plus courte qui
 * situe, et une liste de quatre éléments ne la remplace pas à l'oreille.
 */
function Progression({ etape }: { etape: number }) {
  const { messages } = useTraduction();
  const jalons = [
    messages.creerDossier.jalon.enfants,
    messages.creerDossier.jalon.garde,
    messages.creerDossier.jalon.invitation,
    messages.creerDossier.jalon.pret,
  ] as const;

  return (
    <>
      <Invisible>{messages.creerDossier.etapeSur4(etape)}</Invisible>
      <ol className="jalons" aria-label="Progression">
        {jalons.map((jalon, index) => {
          const rang = index + 1;
          const etat = rang < etape ? 'fait' : rang === etape ? 'courant' : 'reste';
          // La classe est composée de bout en bout plutôt qu'interpolée : le contrôle qui
          // vérifie que toute classe du balisage est peinte lit des littéraux, et une
          // interpolation lui ferait prendre le nom de la variable pour une classe.
          const classe =
            etat === 'fait'
              ? 'jalon jalon-fait'
              : etat === 'courant'
                ? 'jalon jalon-courant'
                : 'jalon jalon-reste';
          const etatLabel =
            etat === 'fait'
              ? messages.creerDossier.etatJalon.termine
              : etat === 'courant'
                ? messages.creerDossier.etatJalon.enCours
                : messages.creerDossier.etatJalon.aVenir;
          return (
            <li key={jalon} className={classe} aria-current={etat === 'courant' ? 'step' : undefined}>
              <span className="jalon-trait" />
              <span className="jalon-nom">
                {jalon}
                <Invisible> — {etatLabel}</Invisible>
              </span>
            </li>
          );
        })}
      </ol>
    </>
  );
}

/**
 * Le libellé d'un champ d'enfant : court à l'œil, complet à l'oreille.
 *
 * À l'écran, « Prénom de l'enfant 1 » répète ce que la disposition montre déjà — le champ
 * est rangé sous l'en-tête de l'enfant, et sur une ligne unique il n'y a personne d'autre.
 * Écrit en entier, il allonge chaque libellé d'un tiers et pousse la saisie vers le bas
 * d'un écran de téléphone.
 *
 * À l'oreille, en revanche, rien ne montre : trois champs « Prénom » à la suite ne se
 * distinguent pas, et le nom de groupe n'est pas annoncé par tous les lecteurs d'écran. Le
 * complément part donc dans la partie invisible du libellé — l'énoncé complet est conservé,
 * et il commence toujours par le texte visible, comme l'exige le critère « intitulé dans
 * le nom » (WCAG 2.5.3).
 *
 * Le rang plutôt que le prénom saisi : un libellé qui change à chaque frappe se réannonce
 * pendant qu'on l'écrit. L'en-tête, lui, porte le prénom.
 */
function libelle(texte: string, index: number, plusieurs: boolean, messages: Messages): ReactNode {
  if (!plusieurs) return texte;
  return (
    <>
      {texte}
      <Invisible>{messages.creerDossier.etape1.suffixeRang(index)}</Invisible>
    </>
  );
}

/**
 * Création d'un dossier, en quatre écrans : les enfants, le schéma de garde,
 * l'invitation de l'autre parent, puis la confirmation.
 *
 * L'invitation est **facultative et reportable**. Un parent qui prépare un dossier n'est
 * pas toujours prêt à écrire à l'autre le jour même, et le produit doit rester utilisable
 * seul (docs/02 §1.4) — le lui refuser reviendrait à faire de l'invitation un péage.
 */
export function CreerDossier({ surCreation }: { surCreation?: (id: string) => void }) {
  const { client } = useSession();
  const { messages } = useTraduction();
  const [etape, setEtape] = useState<1 | 2 | 3 | 4>(1);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const [enfants, setEnfants] = useState<EnfantSaisi[]>(() => [enfantVide()]);
  const [schema, setSchema] = useState<SchemaDeGarde>('alternating_week');
  const [nidation, setNidation] = useState(false);
  const [debut, setDebut] = useState(aujourdhui);
  const [heure, setHeure] = useState('16:30');
  const [quiCommence, setQuiCommence] = useState<'1' | '2'>('1');
  const [residence, setResidence] = useState<Residence>(residenceParDefaut);
  const [emailAutreParent, setEmailAutreParent] = useState('');
  const [dossierId, setDossierId] = useState<string | null>(null);
  const [invitationEnvoyee, setInvitationEnvoyee] = useState(false);
  const ajouterRef = useRef<HTMLButtonElement>(null);

  const enfantsValides = enfants.filter((e) => e.firstName.trim() && e.birthDate);

  function majEnfant(cle: number, champ: 'firstName' | 'birthDate', valeur: string): void {
    setEnfants((precedents) =>
      precedents.map((enfant) => (enfant.cle === cle ? { ...enfant, [champ]: valeur } : enfant)),
    );
  }

  /**
   * Retirer une ligne. Sans confirmation : la saisie n'est pas encore enregistrée, et
   * demander « êtes-vous sûr ? » pour un champ vide ajouté par erreur coûterait plus cher
   * que le geste lui-même. Le retour se fait en ré-ajoutant.
   */
  function retirerEnfant(cle: number): void {
    setEnfants((precedents) => precedents.filter((enfant) => enfant.cle !== cle));
    // Le bouton retiré emportait le focus avec lui : sans cette reprise, il retombe sur le
    // corps du document et la navigation au clavier repart du haut de la page.
    ajouterRef.current?.focus();
  }

  async function creer(): Promise<void> {
    setErreur(null);
    setEnvoi(true);
    try {
      const dossier = await client.request<DossierCree>('/case-files', {
        method: 'POST',
        // Sans ce tri, la clé de ligne — purement locale — partirait dans la charge utile.
        body: {
          children: enfantsValides.map(({ firstName, birthDate }) => ({ firstName, birthDate })),
        },
      });
      setDossierId(dossier.id);

      const [h, m] = heure.split(':');
      await client.request(`/case-files/${dossier.id}/custody/schedule`, {
        method: 'POST',
        body: {
          kind: schema,
          params: {
            startsOn: debut,
            handoverTime: { hour: Number(h), minute: Number(m) },
            startingParentSlot: Number(quiCommence),
            ...(nidation ? { nesting: true } : {}),
            // La zone de vacances scolaires n'est jamais envoyée : le serveur la déduit
            // lui-même du pays et de la ville, voir `deriveSchoolZone`.
            ...(residence.country ? { residenceCountry: residence.country } : {}),
            ...(residence.city.trim() ? { residenceCity: residence.city.trim() } : {}),
          },
        },
      });

      setEtape(3);
    } catch (cause) {
      setErreur(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  }

  async function inviter(): Promise<void> {
    if (!dossierId) return;
    setErreur(null);
    setEnvoi(true);
    try {
      await client.request(`/case-files/${dossierId}/invitations`, {
        method: 'POST',
        body: { email: emailAutreParent },
      });
      setInvitationEnvoyee(true);
      setEtape(4);
    } catch (cause) {
      setErreur(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <>
      {/* Le titre s'écrit désormais dans le bandeau, à la place du mot-symbole
          (`Coquille.tsx`, `TITRES_STATIQUES`). Il reste ici comme `<h1>` invisible : le
          plan du document ne doit pas venir d'un bandeau qui vit hors du `<main>` et se
          répète d'un écran à l'autre. Le dessin change, le plan ne change pas. */}
      <h1 className="invisible">{messages.creerDossier.titre}</h1>
      <Progression etape={etape} />

      {erreur ? <Alerte titre={messages.creerDossier.erreurEtape}>{erreur}</Alerte> : null}

      {etape === 1 ? (
        <Carte titre={messages.creerDossier.etape1.titreCarte}>
          {/* La mise en garde qui vivait sous « Nouveau dossier » sur l'accueil. Le bouton
              est parti au menu, où une entrée ne porte qu'un libellé ; l'avertissement se
              dit donc ici, en tête du premier écran de la création — avant la première
              saisie, tant qu'il ne coûte rien de refermer. Un dossier réunit les enfants
              qu'on a avec **un même** autre parent : sans cette phrase, un parent en ouvre
              un second pour un petit frère, puis découvre deux calendriers, deux
              messageries et deux historiques pour une seule famille. */}
          <p className="discret" style={{ marginTop: 0 }}>
            {messages.creerDossier.etape1.aideFratrie}
          </p>
          {enfants.map((enfant, index) => {
            const prenom = enfant.firstName.trim();
            return (
              <div className="enfant-saisi" key={enfant.cle}>
                {/* L'en-tête n'apparaît qu'à partir du deuxième enfant. Sur une ligne unique
                    il n'y aurait rien à distinguer et rien à retirer — un titre « Enfant 1 »
                    au-dessus de deux champs ne dirait que ce que la carte dit déjà. */}
                {enfants.length > 1 ? (
                  <div className="enfant-saisi-tete">
                    {/* Le prénom saisi remplace le rang dès qu'il est là : avec trois lignes
                        à l'écran, « Zoé » se retrouve d'un coup d'œil, « Enfant 2 » se
                        compte. */}
                    <h3>{prenom || messages.creerDossier.etape1.enfantParDefaut(index)}</h3>
                    <Bouton variante="lien" onClick={() => retirerEnfant(enfant.cle)}>
                      {messages.creerDossier.etape1.retirer}
                      <Invisible>{messages.creerDossier.etape1.retirerSuffixe(index, prenom)}</Invisible>
                    </Bouton>
                  </div>
                ) : null}
                <Champ
                  label={libelle(messages.creerDossier.etape1.prenom, index, enfants.length > 1, messages)}
                  value={enfant.firstName}
                  onChange={(e) => majEnfant(enfant.cle, 'firstName', e.target.value)}
                  /* Le prénom d'un enfant n'est pas une donnée que le navigateur connaît :
                     laissée ouverte, la saisie automatique propose celui du parent. */
                  autoComplete="off"
                  required
                />
                <ChampDateNaissance
                  label={libelle(
                    messages.creerDossier.etape1.dateNaissance,
                    index,
                    enfants.length > 1,
                    messages,
                  )}
                  value={enfant.birthDate}
                  onChange={(valeur) => majEnfant(enfant.cle, 'birthDate', valeur)}
                  traduction={messages.dates}
                  required
                />
              </div>
            );
          })}

          {/* Les deux actions ne sont pas de même nature, et ne se rangent donc pas côte à
              côte : « Ajouter » agit sur la liste au-dessus, « Continuer » quitte l'écran.
              Séparées, elles cessent de se disputer le geste suivant — et sur un téléphone
              elles s'empilent au lieu de passer à la ligne dans un ordre imprévisible. */}
          <div className="etape-actions">
            <Bouton
              ref={ajouterRef}
              variante="secondaire"
              onClick={() => setEnfants((precedents) => [...precedents, enfantVide()])}
            >
              {messages.creerDossier.etape1.ajouter}
            </Bouton>
            <Bouton onClick={() => setEtape(2)} disabled={enfantsValides.length === 0}>
              {messages.creerDossier.etape1.continuer}
            </Bouton>
          </div>
        </Carte>
      ) : null}

      {etape === 2 ? (
        <Carte titre={messages.creerDossier.etape2.titreCarte}>
          <p className="discret" style={{ marginTop: 0 }}>
            {messages.creerDossier.etape2.aideModification}
          </p>

          <div className="champ">
            <label htmlFor="schema">{messages.creerDossier.etape2.schema}</label>
            <select
              id="schema"
              value={schema}
              onChange={(e) => setSchema(e.target.value as SchemaDeGarde)}
            >
              {ORDRE_FAMILLES.map((famille) => {
                const options = SCHEMAS.filter((option) => option.famille === famille);
                if (options.length === 0) return null;
                return (
                  <optgroup key={famille} label={messages.schemasDeGarde.familles[famille]}>
                    {options.map((option) => (
                      <option key={option.kind} value={option.kind}>
                        {messages.schemasDeGarde.schemas[option.kind].libelle}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            <p className="discret" style={{ marginBottom: 0 }}>
              {messages.schemasDeGarde.schemas[schema].detail}
            </p>
          </div>

          <div className="champ">
            <label>
              <input
                type="checkbox"
                checked={nidation}
                onChange={(e) => setNidation(e.target.checked)}
              />{' '}
              {messages.creerDossier.etape2.nidation}
            </label>
            <p className="discret" style={{ marginBottom: 0 }}>
              {messages.creerDossier.etape2.nidationAide}
            </p>
          </div>

          {/* Sert aujourd'hui à afficher la zone de vacances scolaires (A/B/C en France) à
              côté du calendrier ; facultatif, comme tout ce qui n'est pas indispensable à
              la création du dossier. */}
          <ChampResidence residence={residence} onChange={setResidence} />

          {/* Un schéma de garde démarre autour d'aujourd'hui, jamais dans un passé lointain. */}
          <ChampDate
            label={messages.creerDossier.etape2.dateDebut}
            value={debut}
            onChange={setDebut}
            required
            fenetre={{ avant: 1, apres: 1 }}
            traduction={messages.dates}
          />
          <Champ
            label={messages.creerDossier.etape2.heure}
            type="time"
            value={heure}
            onChange={(e) => setHeure(e.target.value)}
            aide={messages.creerDossier.etape2.heureAide}
            required
          />

          <div className="champ">
            <label htmlFor="qui-commence">{messages.creerDossier.etape2.quiCommence}</label>
            <select
              id="qui-commence"
              value={quiCommence}
              onChange={(e) => setQuiCommence(e.target.value === '2' ? '2' : '1')}
            >
              <option value="1">{messages.creerDossier.etape2.moi}</option>
              <option value="2">{messages.creerDossier.etape2.autreParent}</option>
            </select>
          </div>

          {/* L'aperçu se place après les trois réglages dont il dépend — le schéma, la date
              de début et qui commence. Posé plus haut, il montrerait une alternance que
              les champs suivants viendraient démentir sous les yeux du parent. */}
          <ApercuAlternance
            schema={schema}
            debut={debut}
            heure={heure}
            quiCommence={quiCommence}
          />

          <div className="etape-actions">
            <Bouton variante="secondaire" onClick={() => setEtape(1)}>
              {messages.creerDossier.etape2.revenir}
            </Bouton>
            <Bouton onClick={() => void creer()} disabled={!debut || envoi}>
              {envoi ? messages.creerDossier.etape2.creationEnCours : messages.creerDossier.etape2.creer}
            </Bouton>
          </div>
        </Carte>
      ) : null}

      {etape === 3 ? (
        <Carte titre={messages.creerDossier.etape3.titreCarte}>
          <p style={{ marginTop: 0 }}>{messages.creerDossier.etape3.intro}</p>
          <p className="discret">{messages.creerDossier.etape3.redaction}</p>

          <Champ
            label={messages.creerDossier.etape3.email}
            type="email"
            value={emailAutreParent}
            onChange={(e) => setEmailAutreParent(e.target.value)}
          />

          <div className="etape-actions">
            <Bouton variante="secondaire" onClick={() => setEtape(4)}>
              {messages.creerDossier.etape3.plusTard}
            </Bouton>
            <Bouton onClick={() => void inviter()} disabled={!emailAutreParent || envoi}>
              {envoi ? messages.creerDossier.etape3.envoiEnCours : messages.creerDossier.etape3.envoyer}
            </Bouton>
          </div>
        </Carte>
      ) : null}

      {etape === 4 ? (
        <Carte titre={messages.creerDossier.etape4.titreCarte}>
          <p style={{ marginTop: 0 }}>
            {invitationEnvoyee
              ? messages.creerDossier.etape4.messageAvecInvitation
              : messages.creerDossier.etape4.messageSansInvitation}
          </p>
          <div className="etape-actions">
            <Bouton onClick={() => dossierId && surCreation?.(dossierId)}>
              {messages.creerDossier.etape4.ouvrir}
            </Bouton>
          </div>
        </Carte>
      ) : null}
    </>
  );
}
